import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { styled } from "~/config/stitches";
import { Box } from "~/design-system/Box";
import { Heading } from "~/design-system/Heading";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text";
import { createComponentHook } from "~/types";
import type { LanguageType } from "../LanguageDropdown/LanguageDropdown";

const NavigationButton = styled("button", {
  "&:focus": {
    boxShadow: "0 0 0 2px $colors$primary500",
    outline: "none",
  },
  "&:hover": {
    backgroundColor: "$colors$gray100",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-2px)",
  },
  backgroundColor: "$colors$white",
  border: "1px solid $colors$gray200",
  borderRadius: "$radius$medium",
  cursor: "pointer",
  margin: "$space$4",
  maxWidth: "450px",
  minHeight: "220px",
  padding: "$space$8",
  transition: "all 0.2s ease-in-out",
  width: "100%",
});

const ButtonContainer = styled("div", {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "center",
  margin: "0 auto",
  maxWidth: "1000px",
  width: "100%",
});

type RightsMainPageProps = {
  language: LanguageType;
};

const useRightsMainPage = createComponentHook((props: RightsMainPageProps) => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  React.useEffect(() => {
    void i18n.changeLanguage(props.language);
  }, [props.language, i18n]);

  const navigateToProject = () => {
    navigate("project");
  };

  const navigateToOrganization = () => {
    navigate("organization");
  };

  return {
    actions: {
      navigateToOrganization,
      navigateToProject,
    },
    t,
  };
});

function RightsMainPage(props: RightsMainPageProps) {
  const { actions, t } = useRightsMainPage(props);

  return (
    <Box css={{ marginTop: "$space$8" }}>
      <Box css={{ marginBottom: "$space$12", textAlign: "center" }}>
        <Heading as="h1" level="1" css={{ fontSize: "2.5rem" }}>
          {t("screens.Rights.title", "Rights")}
        </Heading>
      </Box>
      <ButtonContainer>
        <NavigationButton onClick={actions.navigateToProject}>
          <Stack space="medium">
            <Heading as="h2" level="2" css={{ fontSize: "1.75rem" }}>
              {t(
                "screens.Rights.components.RightsMainPage.projectButton",
                "Project"
              )}
            </Heading>
            <Text
              color="gray"
              css={{ fontSize: "1.1rem", marginTop: "$space$2" }}
            >
              {t(
                "screens.Rights.components.RightsMainPage.projectDescription",
                "View all project-level permissions and roles"
              )}
            </Text>
          </Stack>
        </NavigationButton>

        <NavigationButton onClick={actions.navigateToOrganization}>
          <Stack space="medium">
            <Heading as="h2" level="2" css={{ fontSize: "1.75rem" }}>
              {t(
                "screens.Rights.components.RightsMainPage.organizationButton",
                "Organization"
              )}
            </Heading>
            <Text
              color="gray"
              css={{ fontSize: "1.1rem", marginTop: "$space$2" }}
            >
              {t(
                "screens.Rights.components.RightsMainPage.organizationDescription",
                "View all organization-level permissions and roles"
              )}
            </Text>
          </Stack>
        </NavigationButton>
      </ButtonContainer>
    </Box>
  );
}

export { RightsMainPage };
